import {minTaskHeight} from '@components/calendar/utils'
import {useDragScrollable} from '@containers/drag-scrollable'
import {noPropagate} from '@containers/hooks'
import {
  addDays,
  endOfDay,
  format,
  getDay,
  isSameDay,
  isWithinInterval,
  startOfDay,
  subDays
} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import useResizeObserver from 'use-resize-observer'
import {DataObjectId} from 'yougile-model'
import {Util} from 'yougile-util'
import {Shift, TaskWithColor, useCalendarStore} from '../../calendar-store'
import {AllDay} from './all-day'
import {daysHeaderBottomLine} from './all-day-placeholder'
import {
  getLongWithTimeTasks,
  getMaxHeight,
  getMultipleDaysTasks,
  getSingleDayTasks,
  TaskTopPlacement
} from './all-day-utils'

const DaysHeaderTw = tw.div<{$disableScroll: boolean}>`
  flex
  min-w-full
  bg-main-old
  w-max
  h-auto
  scrollbar-x-hidden
  scrollbar-track-input-disabled
  scrollbar-thumb-obscure-constant
  scrollbar-track-x-transparent
  scrollbar-thumb-x-transparent
  ${p => (p.$disableScroll ? 'overflow-hidden' : 'scrollbar')}
`

export const DayHeadTw = tw.div`
  flex
  flex-col
  min-w-200
  grow-0
  basis-1/7
  w-1/7
  relative
`

const DividerTw = tw.div`
  absolute
  top-0
  right-0
  w-1
  bg-input-disabled-old
  pointer-events-none
`

export const taskHeight = minTaskHeight
export const dateFormat = 'yyyy-MM-dd'

type AllDayType =
  | 'single-day'
  | 'multiple-days'
  | 'long-with-time'
  | 'multiple-days-for-monday'
  | 'long-with-time-for-monday'

export interface TasksAllDay extends TaskWithColor {
  id: DataObjectId
  duration: number
  allDayType: AllDayType
  top?: number
}

interface Props {
  shift: Shift
}

// eslint-disable-next-line max-lines-per-function
export const AllDayTasks = observer(({shift}: Props) => {
  const [refScrollable, mouseDown] = useDragScrollable({
    direction: 'scroll-y',
    shouldActivate: true
  })
  const {ref, width = 1} = useResizeObserver()
  const [state, store] = useCalendarStore()
  useEffect(() => {
    if (!state.showMoreAllDayTasks) {
      refScrollable.current.scrollTop = 0
    }
  }, [state.showMoreAllDayTasks])

  const singleDay = getSingleDayTasks(store, shift)
  const multipleDays = getMultipleDaysTasks(store, shift).filter(
    task => task.duration !== -1
  )
  const longWithTime = getLongWithTimeTasks(store, shift).filter(
    task => task.duration !== -1
  )

  const maxHeight = getMaxHeight(state.showMoreAllDayTasks)

  const topPlacement = new TaskTopPlacement()

  const tasksForMonday: TasksAllDay[] = []

  const multipleDaysForMonday = multipleDays.map(task => {
    const day = getDay(task.roundedDeadline)
    return {
      ...task,
      duration:
        task.roundedStartDate < store.startDate('current') &&
        task.roundedDeadline > endOfDay(store.endDate('current'))
          ? 7
          : day === 0
          ? 7
          : day,
      allDayType: 'multiple-days-for-monday' as AllDayType
    }
  })

  const longWithTimeForMonday = longWithTime.map(task => ({
    ...task,
    duration:
      task.roundedStartDate < store.startDate('current') &&
      task.roundedDeadline > endOfDay(store.endDate('current'))
        ? 7
        : getDay(subDays(task.roundedDeadline, 1)),
    allDayType: 'long-with-time-for-monday' as AllDayType
  }))

  new Array(7).fill(null).map((_, day) => {
    const start = startOfDay(addDays(store.startDate(shift), day))
    tasksForMonday.push(
      ...[
        ...multipleDaysForMonday.filter(task => {
          return (
            (task.roundedStartDate < store.startDate('current') &&
              task.roundedDeadline > endOfDay(store.endDate('current')) &&
              getDay(start) === 1) ||
            (format(task.roundedDeadline, dateFormat) ===
              format(start, dateFormat) &&
              !isWithinInterval(task.roundedStartDate, {
                start: store.startDate(shift),
                end: endOfDay(store.endDate(shift))
              }))
          )
        }),
        ...longWithTimeForMonday.filter(task => {
          return (
            (task.roundedStartDate < store.startDate('current') &&
              task.roundedDeadline > endOfDay(store.endDate('current')) &&
              day === 0) ||
            (format(subDays(task.roundedDeadline, 1), dateFormat) ===
              format(start, dateFormat) &&
              !isWithinInterval(addDays(task.roundedStartDate, 1), {
                start: store.startDate(shift),
                end: endOfDay(store.endDate(shift))
              }) &&
              !isSameDay(task.roundedDeadline, start) &&
              !isSameDay(task.roundedStartDate, start) &&
              task.roundedStartDate < start &&
              task.roundedDeadline > start)
          )
        })
      ]
    )
  })

  const tasksForMondayWithoutDuplicates =
    Util.removeDuplicateIds(tasksForMonday)

  return (
    <>
      <DaysHeaderTw
        ref={refScrollable}
        onMouseDown={e => {
          state.showMoreAllDayTasks && mouseDown(e)
          noPropagate(e)
        }}
        $disableScroll={!state.showMoreAllDayTasks}
      >
        <div
          className="flex min-w-full w-max"
          style={{maxHeight, height: state.allDayTasksHeight}}
        >
          {new Array(7).fill(null).map((_, day) => {
            const start = startOfDay(addDays(store.startDate(shift), day))

            const allTasks = [
              ...multipleDays.filter(task => {
                return (
                  format(task.roundedStartDate, dateFormat) ===
                  format(start, dateFormat)
                )
              }),
              ...singleDay.filter(task => {
                return (
                  format(+task.task.deadline, dateFormat) ===
                  format(start, dateFormat)
                )
              }),
              ...longWithTime.filter(task => {
                return (
                  format(addDays(task.roundedStartDate, 1), dateFormat) ===
                    format(start, dateFormat) &&
                  !isSameDay(task.roundedDeadline, start) &&
                  !isSameDay(task.roundedStartDate, start) &&
                  task.roundedStartDate < start &&
                  task.roundedDeadline > start
                )
              })
            ]

            day === 0
              ? topPlacement.calculate(
                  [...tasksForMondayWithoutDuplicates, ...allTasks],
                  0
                )
              : topPlacement.calculate(allTasks, day)

            return (
              <AllDay
                ref={ref}
                key={day}
                allTasks={allTasks}
                start={start}
                shift={shift}
                width={width}
                topPlacementHeight={topPlacement.height}
                tasksForMonday={tasksForMondayWithoutDuplicates}
              />
            )
          })}
        </div>
        <div className="absolute left-0 flex min-w-full">
          {new Array(7).fill(null).map((_, day) => (
            <DayHeadTw key={day} style={{maxHeight}}>
              <DividerTw style={{height: state.allDayTasksHeight}} />
            </DayHeadTw>
          ))}
        </div>
      </DaysHeaderTw>
      {state.allDayTasksHeight > 3 && daysHeaderBottomLine}
    </>
  )
})
